import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Blaze, Button, Card, List, ListItem, ListItemText, Paper, StackColumn, useSnackbar } from '@dhl-ui/components'
import { PaperplanePhoneComputerIllustration } from '@dhl-ui/illustrations'
import { NativeModal } from '@dhl/native-modal'
import { usePutProfileMobilePhoneValidate } from '@dhl/receiver-profile-api'
import { useSessionStorage } from '@dhl/use-storage'

import { useGetProfile } from '~/profile/model/profile/hooks.js'
import { links } from '~/links.js'

const validateMobileNumberDismissedKey = 'portal-validate-mobile-number-dismissed'

export const ValidateMobileNumberInteraction: FC = () => {
  const [open, setOpen] = useState(true)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: profile } = useGetProfile()
  const { mutateAsync: validateMobilePhone } = usePutProfileMobilePhoneValidate()
  const { enqueueSnackbar } = useSnackbar()
  const [, setDismissed] = useSessionStorage<boolean>(validateMobileNumberDismissedKey, false)

  const additionalEmails = profile?.emailAddresses?.filter(({ isPrimary }) => !isPrimary) ?? []

  const handleConfirm = async () => {
    await validateMobilePhone()
    enqueueSnackbar(t('portal.interaction.validateProfile.confirmSnackbar'), { severity: 'success' })
    setOpen(false)
  }

  const handleChange = () => {
    setDismissed(true)
    setOpen(false)
    navigate(links.settingsLink())
  }

  return (
    <NativeModal open={open}>
      <NativeModal.Content>
        <StackColumn gap={2}>
          <Paper sx={{ p: 3, pb: 1 }}>
            <Blaze
              description={t('portal.interaction.validateProfile.description')}
              slotProps={{ illustration: { variant: PaperplanePhoneComputerIllustration } }}
              title={t('portal.interaction.validateProfile.title')}
              sx={{ m: '0 auto' }}
            />
          </Paper>
          <Card background="dark">
            <Card.Header title={t('portal.interaction.validateProfile.mobileNumberHeader')} />
            <Card.Content>
              <ListItemText primary={profile?.mobilePhone?.number} />
            </Card.Content>
          </Card>
          {additionalEmails.length > 0 && (
            <Card background="dark">
              <Card.Header title={t('portal.interaction.validateProfile.additionalEmailsHeader')} />
              <Card.Content noPaddingBottom noPaddingSides noPaddingTop>
                <List>
                  {additionalEmails.map(({ emailId, email }) => (
                    <ListItem background="dark" key={emailId}>
                      <ListItemText primary={email} />
                    </ListItem>
                  ))}
                </List>
              </Card.Content>
            </Card>
          )}
        </StackColumn>
      </NativeModal.Content>
      <NativeModal.Footer>
        <Button fullWidth data-testid="validate-profile-confirm" variant="contained" onClick={handleConfirm}>
          {t('portal.interaction.validateProfile.confirmButton')}
        </Button>
        <Button fullWidth color="dark" data-testid="validate-profile-change" variant="text" onClick={handleChange}>
          {t('portal.interaction.validateProfile.changeButton')}
        </Button>
      </NativeModal.Footer>
    </NativeModal>
  )
}
